import { Badge, Button, Tooltip } from '@/components';
import { useCommentPageTransitionParamStates } from '@/features/comment/hooks/page-transition-param-states';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import type { CommentStackSiderViewConfigType } from '@/features/comment/types/stack-sider-view';
import { useProjectNotificationBadges } from '@/features/project/_hooks/notification';
import { useId } from '@/hooks';
import { useStackSiderView } from '@/providers/StackSiderViewProvider';
import { CommentOutlined } from '@ant-design/icons';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  peopleInChargeIds?: string[] | null;
  filteredOrganizationFiscalYear: number | undefined;
};

export const CommentSiderButton: FC<Props> = ({
  peopleInChargeIds,
  filteredOrganizationFiscalYear,
}) => {
  const { t } = useTranslation();
  const { id } = useId();

  const isFirstSiderOpen = useRef(true);
  const [{ commentId }] = useCommentPageTransitionParamStates();
  const notification = useProjectNotificationBadges({ id });
  const notificationReplyUnreadCount = notification.data?.comment.unread;

  const stackSiderView = useStackSiderView();
  const commentStackSiderView = useCommentStackSiderView();

  const target: CommentStackSiderViewConfigType['target'] = useMemo(
    () => ({
      id,
      key: 'project',
      objectAssigneeIds: peopleInChargeIds,
      projectFiscalYear: filteredOrganizationFiscalYear,
    }),
    [id, peopleInChargeIds, filteredOrganizationFiscalYear],
  );

  const handleClick = () => {
    commentStackSiderView.push({
      payload: {
        config: {
          target,
          viewType: 'comment-list',
        },
      },
      target: {
        type: 'project',
        projectId: id,
      },
    });
  };

  useEffect(() => {
    if (isFirstSiderOpen.current) {
      isFirstSiderOpen.current = false;
      if (commentId) {
        commentStackSiderView.set({
          payload: [
            {
              config: {
                target,
                viewType: 'comment-list',
              },
              siderType: 'comment',
            },
            {
              config: {
                target,
                id: commentId,
                viewType: 'comment-details',
              },
              siderType: 'comment',
            },
          ],
          target: {
            type: 'project',
            projectId: id,
          },
        });
      }
    }
  }, [commentId, commentStackSiderView, target, id]);

  return (
    <Tooltip title={t('comment')}>
      <Badge
        color="red"
        count={notificationReplyUnreadCount}
        size="small"
        style={{
          boxShadow: 'none',
          paddingInline: 4,
        }}
        offset={[
          !!notificationReplyUnreadCount && notificationReplyUnreadCount < 10
            ? -4
            : -10,
          4,
        ]}
      >
        <Button
          onClick={handleClick}
          disabled={stackSiderView.state.stack.length > 0}
          icon={<CommentOutlined aria-label="" />}
        />
      </Badge>
    </Tooltip>
  );
};
